/**
 * World: the game's 3D entry view (MASTER_PROMPT §6.1, Phase 3).
 *
 *   Globe     all 30 markets. Colour = your status there; pillars = your
 *             revenue share; red bars = rivals and their share; rings =
 *             the economic cycle; arcs = foreign-currency revenue (gold =
 *             hedged); the beam marks HQ. Tap a market; double-tap to land.
 *   City      the market's district: your HQ (floors = headcount), your
 *             industry's sites and landmark (backend/industry_kits.py),
 *             rivals (height = market share), bank, government, exchange,
 *             supplier depots and the traffic between them. Real
 *             bottlenecks queue the traffic and glow red.
 *   Interior  HQ cut away: one zone per department, one desk per person; the
 *             operations team works on the industry's own floor.
 *
 * In Lite mode (or without WebGL) the same map is drawn in 2D and the City
 * and Interior facts are listed as text. The side panel and the markets
 * table always say in words what the 3D shows (accessibility; colour is
 * never the only signal). Data: GET /api/world (backend/world_view.py).
 */
import { API } from "../api.js";
import { is3D, reportLowFps } from "../app/display.js";
import { showTab } from "../app/shell.js";
import { esc, fmtMoney } from "../ui/format.js";
import { toast } from "../ui/modals.js";
import { openCountryExpansionModal } from "./core/government.js";
import { countryAt, decodeTopology, marketForAtlas, pixelToLatLon } from "../world/geo.js";
import { STATUS_COLOR, paintMap } from "../world/mapart.js";
import { FLOOR, hqFloors } from "../world/buildgen.js";
import { WEATHER_ICON } from "../world/weatherspec.js";

const WD_STATUS_LABEL = { hq: "Headquarters", operating: "Operating", establishing: "Establishing", available: "Not entered yet" };
const WD_LEVEL_LABEL = { globe: "Globe", city: "District", interior: "Inside HQ" };

let wdCurrent = null;      // the live 3D world, disposed when the tab re-renders
let wdTopology = null;     // decoded country shapes, fetched once
let wdState = { market: null, level: "globe", pick: null };

async function wdCountries() {
  if (!wdTopology) {
    const topo = await fetch("/static/assets/geo/countries-110m.json").then((r) => r.json());
    wdTopology = decodeTopology(topo);
  }
  return wdTopology;
}

const wdPct = (v, d = 1) => `${(+v || 0).toFixed(d)}%`;

/** The side panel for the current selection: everything the 3D shows, in words. */
function wdPanel(world, state) {
  if (!world) return "";
  const c = (world.countries || []).find((x) => x.country === state.market);
  if (state.level !== "globe") return wdLevelPanel(world, state);
  if (!c) {
    const live = (world.countries || []).filter((x) => x.status !== "available");
    return `
      <div class="wd-panel-head"><span class="wd-kicker">Your world · month ${world.month}</span><h3>${esc(world.company.name)}</h3></div>
      <p class="wd-muted">${live.length} of ${world.countries.length} markets active. ${wdPct(world.international_exposure_pct)} of revenue earned abroad.</p>
      <ul class="wd-legend">
        <li><i style="background:${STATUS_COLOR.hq}"></i>HQ (beam) · <i style="background:${STATUS_COLOR.operating}"></i>operating · <i style="background:${STATUS_COLOR.establishing}"></i>establishing · <i style="background:${STATUS_COLOR.available}"></i>open to enter</li>
        <li><b>Pillar</b> height = your share of revenue there</li>
        <li><b class="neg">Red bars</b> = rivals there, height = their market share</li>
        <li><b>Ring</b> colour = economic cycle (green expansion → red slowdown)</li>
        <li><b>Arc</b> = revenue in a foreign currency; gold = hedged, moving dot = unhedged</li>
      </ul>
      <p class="wd-hint">Tap a market to inspect it. Double-tap to land in its district.</p>`;
  }
  const m = c.macro;
  const rivals = c.rivals.length
    ? `<ul class="wd-list">${c.rivals.slice(0, 6).map((r) => `<li><span>${esc(r.name)}</span><b>${wdPct(r.market_share_pct)}</b></li>`).join("")}${c.rivals.length > 6 ? `<li class="wd-muted">+${c.rivals.length - 6} more</li>` : ""}</ul>`
    : `<p class="wd-muted">${c.status === "available" ? "No rival has followed you here." : "No rivals here yet."}</p>`;
  const actions = [];
  if (c.status === "available") actions.push(`<button class="primary" data-wd-act="enter">Explore entering ${esc(c.country)}</button>`);
  if (c.status === "hq" || c.status === "operating") actions.push(`<button class="primary" data-wd-act="city">${c.status === "hq" ? "Enter your district" : "See your district"}</button>`);
  if (c.status === "hq") actions.push(`<button data-wd-act="interior">Walk into HQ</button>`);
  if (c.fx) actions.push(`<button data-wd-act="fx">Currency exposure</button>`);
  actions.push(`<button data-wd-act="macro">Global economy</button>`);
  return `
    <div class="wd-panel-head">
      <span class="wd-kicker wd-st-${c.status}">${WD_STATUS_LABEL[c.status]}</span>
      <h3>${esc(c.country)}</h3>
      <button class="wd-x" data-wd-act="clear" aria-label="Clear selection">✕</button>
    </div>
    ${c.expansion ? `<div class="wd-progress"><span>Establishing · ${esc(c.expansion.entry_mode.replace(/_/g, " "))}</span><div><i style="width:${Math.round(100 * c.expansion.months_elapsed / Math.max(1, c.expansion.months_total))}%"></i></div><small>Month ${c.expansion.months_elapsed} of ${c.expansion.months_total}</small></div>` : ""}
    <div class="wd-stats">
      <div><span>Your revenue share</span><b>${wdPct(c.revenue_share_pct)}</b></div>
      <div><span>Est. revenue / month</span><b>${fmtMoney(c.estimated_revenue)}</b></div>
      <div><span>Economy</span><b class="wd-cycle-${esc(m.cycle_phase)}">${esc(m.cycle_phase)}</b></div>
      ${c.weather ? `<div><span>Weather</span><b>${WEATHER_ICON[c.weather.sky] || ""} ${esc(c.weather.label)}</b></div>` : ""}
      <div><span>GDP growth</span><b>${wdPct(m.gdp_growth_pct, 2)}</b></div>
      <div><span>Inflation</span><b>${wdPct(m.inflation_pct, 2)}</b></div>
      <div><span>Interest rate</span><b>${wdPct(m.interest_rate_pct, 2)}</b></div>
      <div><span>Consumer confidence</span><b>${m.consumer_confidence}/100</b></div>
      <div><span>Corporate tax</span><b>${wdPct(c.tax_rate * 100, 0)}</b></div>
      <div><span>Currency</span><b>${esc(c.currency)}${c.fx ? (c.fx.hedged ? " · hedged" : " · unhedged") : ""}</b></div>
      ${c.localized_products || c.government_deals ? `<div><span>Local products · gov. deals</span><b>${c.localized_products} · ${c.government_deals}</b></div>` : ""}
    </div>
    <h4>Rivals ${c.status === "hq" ? "in your home market" : "here"}</h4>
    ${rivals}
    <div class="wd-actions">${actions.join("")}</div>`;
}

/** Phase 5: how the business runs, in words: bottlenecks first, then capacity, supply and the value chain. */
function wdOpsHtml(world) {
  const kit = world.kit || {};
  const cap = world.capacity;
  const out = [];
  if ((world.bottlenecks || []).length) {
    out.push(`<h4 class="neg">Bottlenecks</h4><ul class="wd-list wd-bottlenecks">${world.bottlenecks.map((b) => `<li class="neg"><span>${esc(b.text)}</span></li>`).join("")}</ul>`);
  }
  if (cap) {
    out.push(`<h4>${esc(cap.capacity_label)}</h4><ul class="wd-list"><li><span>${esc(cap.utilization_label)}</span><b class="${cap.band === "critical" ? "neg" : ""}">${wdPct(cap.utilization_pct)} (target ${wdPct(cap.target_utilization_pct, 0)})</b></li></ul>`);
  }
  if (world.supply && world.supply.inputs.length) {
    out.push(`<h4>Supply · ${esc((kit.flow && kit.flow.label) || "deliveries")}</h4><ul class="wd-list">${world.supply.inputs.map((i) =>
      `<li><span>${esc(i.label)} · ${esc(i.strategy || "")}</span><b class="${i.shortage_pct > 0 ? "neg" : ""}">${i.shortage_pct > 0 ? `${i.shortage_pct}% short` : "flowing"}</b></li>`).join("")}
      <li><span>Inventory on hand</span><b>${Math.round(world.supply.inventory_days || 0)} days</b></li></ul>`);
  }
  if ((world.chain || []).length) {
    out.push(`<h4>How you make it</h4><ol class="wd-chain">${world.chain.map((c) =>
      `<li><span>${esc(c.stage)}</span><b>${c.choice ? esc(c.choice) : "not set yet"}</b>${c.tier != null ? `<i style="width:${(100 * (c.tier + 1)) / c.tiers}%"></i>` : ""}</li>`).join("")}</ol>`);
  }
  return out.join("");
}

/** City / Interior facts as text (the panel in 3D, the whole view in Lite). */
function wdLevelPanel(world, state) {
  const s = world.sites || {};
  const market = state.market || world.company.hq_country;
  const isHq = market === world.company.hq_country;
  if (state.level === "interior") {
    const depts = (s.departments || []).filter((d) => d.headcount > 0);
    return `
      <div class="wd-panel-head"><span class="wd-kicker">Inside HQ</span><h3>${esc(world.company.name)}</h3></div>
      <p class="wd-muted">${depts.length
        ? `${s.headcount} people (${s.executives} of them executives${s.hub_engineers ? `, ${s.hub_engineers} engineers in your hubs abroad` : ""}). One desk per person here${s.headcount > 1200 ? ` (above 1,200, one desk stands for ${Math.ceil(s.headcount / 1200)})` : ""}.`
        : "Just you so far: one desk, yours. Hire in Team &amp; Talent and the floor fills up."}</p>
      ${world.kit && world.kit.interior.kind !== "office" ? `<p class="wd-muted">Your operations team works on the ${esc(world.kit.interior.label.toLowerCase())}${world.capacity ? `, running at ${wdPct(world.capacity.utilization_pct)}` : ""}. Red stations mean over capacity; dim ones are idle.</p>` : ""}
      <ul class="wd-list">${depts.map((d) => `<li><span>${esc(d.department)}</span><b>${d.headcount}</b></li>`).join("")}</ul>
      <div class="wd-actions"><button data-wd-act="people">Team &amp; Talent</button><button data-wd-act="operations">Operations</button><button data-wd-act="city">Back to the district</button></div>`;
  }
  const c = (world.countries || []).find((x) => x.country === market) || { rivals: [] };
  const rivals = isHq ? world.rivals : c.rivals;
  const floors = hqFloors(s.headcount);
  const picked = state.pick ? wdDescribePick(world, state.pick, rivals) : "";
  return `
    <div class="wd-panel-head"><span class="wd-kicker">District</span><h3>${esc(market)}</h3></div>
    ${picked}
    <ul class="wd-list">
      ${isHq ? `<li><span>Your HQ</span><b>${floors} floors · ${s.headcount} people</b></li>
      <li><span>Your sites</span><b>${(s.factories || []).length ? `${s.factories.length} factories` : `${Math.max(1, s.products || 0)} (one per product)`}</b></li>`
      : `<li><span>Your regional office</span><b>${wdPct(c.revenue_share_pct)} of revenue</b></li>`}
      <li><span>Rival towers</span><b>${rivals.length}</b></li>
    </ul>
    ${rivals.length ? `<h4>Skyline: height = market share</h4><ul class="wd-list">${rivals.slice(0, 10).map((r) => `<li><span>${esc(r.name)}</span><b>${wdPct(r.market_share_pct)}</b></li>`).join("")}</ul>` : ""}
    ${isHq ? wdOpsHtml(world) : ""}
    <p class="wd-hint">${world.kit ? `${esc(world.kit.site.label)}s are your sites; ${esc(world.kit.flow.label.toLowerCase())} show what moves, queued and red where something is stuck. ` : ""}HQ floors = 3 + one per 25 people (${FLOOR * 4} m a floor). Grey blocks are scenery.</p>
    <div class="wd-actions">${isHq ? `<button class="primary" data-wd-act="interior">Walk into HQ</button><button data-wd-act="operations">Operations</button>` : ""}<button data-wd-act="competitors">Competitors</button><button data-wd-act="globe">Back to the globe</button></div>`;
}

function wdDescribePick(world, ref, rivals) {
  const s = world.sites || {};
  if (ref === "hq") return `<div class="wd-pick"><b>Your HQ</b><span>${s.headcount} people · ${s.executives} executives · ${s.products} products</span></div>`;
  if (ref.startsWith("rival:")) {
    const r = rivals[+ref.split(":")[1]];
    return r ? `<div class="wd-pick neg"><b>${esc(r.name)}</b><span>${wdPct(r.market_share_pct)} share · ${esc(r.strategy)}</span></div>` : "";
  }
  if (ref.startsWith("site:")) {
    const f = (s.factories || [])[+ref.split(":")[1]];
    return f ? `<div class="wd-pick"><b>${esc(f.name)}</b><span>${Math.round(f.utilization_pct)}% utilised · ${f.capacity_units} units · ${esc(f.status)}</span></div>`
      : `<div class="wd-pick"><b>Your site</b><span>Runs your products in this district.</span></div>`;
  }
  if (ref === "hero" && world.kit) return `<div class="wd-pick"><b>${esc(world.kit.hero.label)}</b><span>The landmark of ${esc(world.company.industry)}.</span></div>`;
  if (ref.startsWith("input:")) {
    const inp = ((world.supply && world.supply.inputs) || []).find((i) => `input:${i.label}` === ref);
    return inp ? `<div class="wd-pick${inp.shortage_pct > 0 ? " neg" : ""}"><b>${esc(inp.label)}</b><span>${esc(inp.strategy || "")} · ${Math.round((inp.share_of_cogs || 0) * 100)}% of your cost of goods${inp.shortage_pct > 0 ? ` · ${inp.shortage_pct}% short this month` : ""}</span></div>` : "";
  }
  const civic = { bank: "Bank district: your lenders (Banking & Debt).", government: "Government: incentives and policy (Government Relations).", exchange: "Stock exchange: listing and investors (Public Markets)." };
  if (civic[ref]) return `<div class="wd-pick"><span>${civic[ref]}</span></div>`;
  if (ref.startsWith("dept:")) {
    const d = (s.departments || []).filter((x) => x.headcount > 0)[+ref.split(":")[1]];
    return d ? `<div class="wd-pick"><b>${esc(d.department)}</b><span>${d.headcount} people</span></div>` : "";
  }
  return "";
}

/** Every market as a row: the text version of the globe. */
function wdTable(world) {
  const order = { hq: 0, operating: 1, establishing: 2, available: 3 };
  const rows = [...world.countries].sort((a, b) => order[a.status] - order[b.status] || b.revenue_share_pct - a.revenue_share_pct || a.country.localeCompare(b.country));
  return `
    <table class="wd-table">
      <thead><tr><th>Market</th><th>Status</th><th>Revenue share</th><th>Economy</th><th>GDP</th><th>Rivals</th><th>Currency</th></tr></thead>
      <tbody>${rows.map((c) => `<tr data-wd-market="${esc(c.country)}" class="${c.country === wdState.market ? "on" : ""}">
        <td><b>${esc(c.country)}</b></td><td><span class="wd-dot" style="background:${STATUS_COLOR[c.status]}"></span>${WD_STATUS_LABEL[c.status]}</td>
        <td>${c.status === "available" ? "—" : wdPct(c.revenue_share_pct)}</td><td>${esc(c.macro.cycle_phase)}</td><td>${wdPct(c.macro.gdp_growth_pct, 1)}</td>
        <td>${c.rivals.length || "—"}</td><td>${esc(c.currency)}${c.fx ? (c.fx.hedged ? " 🔒" : "") : ""}</td></tr>`).join("")}</tbody>
    </table>`;
}

/** Lite / no-WebGL: the same map in 2D, tap to select. */
function wdDraw2D(canvas, countries, world) {
  const w = canvas.clientWidth || 800;
  const h = Math.round(w / 2);
  const ratio = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.round(w * ratio); canvas.height = Math.round(h * ratio);
  canvas.style.height = `${h}px`;
  const ctx = canvas.getContext && canvas.getContext("2d");
  if (ctx) paintMap(ctx, canvas.width, canvas.height, countries, world, { highlight: wdState.market });
}

async function renderWorld(main) {
  if (wdCurrent) { wdCurrent.dispose(); wdCurrent = null; }
  const three = is3D();
  main.innerHTML = `<div class="wd-root">
    <div class="section-title">World <span class="wd-sub">${three ? "3D" : "2D (Lite display)"}</span></div>
    <div class="wd-wrap">
      <div class="wd-stage ${three ? "is-3d" : "is-2d"}">
        <div class="wd-toolbar" role="toolbar" aria-label="World level">
          ${["globe", "city", "interior"].map((l) => `<button data-wd-level="${l}" class="${l === "globe" ? "on" : ""}"${three ? "" : (l === "globe" ? "" : " disabled title=\"3D only: the panel lists these facts\"")}>${WD_LEVEL_LABEL[l]}</button>`).join("")}
          ${three ? `<button data-wd-act="perf" class="wd-perf-btn" title="Frame rate, draw calls and triangles">Perf</button>` : ""}
        </div>
        <div class="wd-view" id="wd-view">${three ? "" : `<canvas class="wd-map" id="wd-map" aria-label="World map: colour shows your status in each market; the table below lists the same."></canvas>`}</div>
        <div class="wd-perf hidden" id="wd-perf"></div>
        <div class="wd-loading" id="wd-loading">Loading the world…</div>
      </div>
      <aside class="wd-panel" id="wd-panel" aria-live="polite"></aside>
    </div>
    <details class="wd-table-wrap" open><summary>All markets</summary><div id="wd-table"></div></details></div>`;

  let world, countries;
  try {
    [world, countries] = await Promise.all([fetch(`${API}/world`).then((r) => r.json()), wdCountries()]);
  } catch (e) {
    main.querySelector("#wd-loading").textContent = "Couldn't load the world: " + e.message;
    return;
  }
  if (!main.querySelector("#wd-view")) return; // tab changed while loading
  wdState = { market: null, level: "globe", pick: null };

  const panel = main.querySelector("#wd-panel");
  const tableEl = main.querySelector("#wd-table");
  const refresh = () => {
    panel.innerHTML = wdPanel(world, wdState);
    tableEl.innerHTML = wdTable(world);
    main.querySelectorAll("[data-wd-level]").forEach((b) => b.classList.toggle("on", b.dataset.wdLevel === wdState.level));
  };

  const select = (market) => {
    wdState.market = market;
    wdState.pick = null;
    if (wdCurrent && wdCurrent.level === "globe") wdCurrent.select(market);
    else if (!wdCurrent) { const c = main.querySelector("#wd-map"); if (c) wdDraw2D(c, countries, world); }
    refresh();
  };

  const hqMarket = world.company.hq_country;
  const go = (lvl) => {
    if (!wdCurrent) { wdState.level = lvl; refresh(); return; } // Lite: the panel carries the facts
    if (lvl === "globe") wdCurrent.showGlobe();
    if (lvl === "city") wdCurrent.showCity(wdState.market && world.countries.find((c) => c.country === wdState.market && c.status !== "available") ? wdState.market : hqMarket);
    if (lvl === "interior") wdCurrent.showInterior();
  };

  // on this view's own root: #main-content outlives the tab, so listeners there would pile up
  main.querySelector(".wd-root").addEventListener("click", (e) => {
    const lvl = e.target.closest("[data-wd-level]");
    if (lvl && !lvl.disabled) return go(lvl.dataset.wdLevel);
    const row = e.target.closest("[data-wd-market]");
    if (row) return select(row.dataset.wdMarket);
    const act = e.target.closest("[data-wd-act]");
    if (!act) return;
    const a = act.dataset.wdAct;
    if (a === "clear") select(null);
    else if (a === "enter") openCountryExpansionModal(wdState.market);
    else if (a === "city" || a === "interior" || a === "globe") go(a);
    else if (a === "fx") showTab("fx");
    else if (a === "macro") showTab("macro");
    else if (a === "people") showTab("executives");
    else if (a === "competitors") showTab("competitors");
    else if (a === "operations") showTab("operations");
    else if (a === "perf") main.querySelector("#wd-perf").classList.toggle("hidden");
  });

  refresh();
  main.querySelector("#wd-loading").remove();

  if (!three) {
    const c = main.querySelector("#wd-map");
    wdDraw2D(c, countries, world);
    c.addEventListener("click", (e) => {
      const r = c.getBoundingClientRect();
      const [lat, lon] = pixelToLatLon(e.clientX - r.left, e.clientY - r.top, r.width, r.height);
      const k = countryAt(countries, lon, lat);
      const market = k && marketForAtlas(k.name);
      if (market && world.countries.some((x) => x.country === market)) select(market);
    });
    return;
  }

  const view = main.querySelector("#wd-view");
  view.classList.add("wd-booting");
  try {
    const { createWorld } = await import("../world/scene.js");
    if (!view.isConnected) return;
    wdCurrent = await createWorld(view, {
      world, countries,
      reducedMotion: !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches),
      onSelect(sel) {
        if (sel.type === "market") { wdState.market = sel.market; wdState.pick = null; }
        else if (sel.ref) wdState.pick = sel.ref;
        else if (sel.type === "ocean" || sel.type === "scenery") wdState.pick = null;
        refresh();
      },
      onLevel(lvl, market) {
        wdState.level = lvl; wdState.pick = null;
        if (lvl !== "globe" && market) wdState.market = market;
        refresh();
      },
      // Lite auto-pick: this device can't hold 30 fps in 3D, so Auto moves it to the 2D map
      onLowFps(fps) {
        if (reportLowFps(fps)) {
          toast(`Switched to Lite: the 3D world ran at ${Math.round(fps)} fps on this device. Pick 3D in Save / Load → Display to keep it.`);
          if (window.ges) window.ges.showTab("world");
        }
      },
    });
  } catch (e) {
    view.innerHTML = `<p class="wd-muted" style="padding:16px;">3D couldn't start (${esc(e.message)}). Switch Display to Lite in Save / Load for the 2D map.</p>`;
    return;
  } finally {
    view.classList.remove("wd-booting");
  }
  const perfEl = main.querySelector("#wd-perf");
  const tick = setInterval(() => {
    if (!wdCurrent || !perfEl.isConnected) return clearInterval(tick);
    if (perfEl.classList.contains("hidden")) return;
    const p = wdCurrent.perf();
    perfEl.innerHTML = `<b>${p.fps}</b> fps (low ${p.low}) · ${p.drawCalls} draw calls · ${(p.triangles / 1000).toFixed(1)}k triangles${p.over.length ? `<br><span class="neg">over budget: ${esc(p.over.join(", "))}</span>` : ""}`;
  }, 500);
}

window.renderWorld = renderWorld;
window.__gesWorld = () => wdCurrent; // for ges.world() (src/app/devtools.js)

export { renderWorld, wdLevelPanel, wdOpsHtml, wdPanel, wdTable };
